import React, { useMemo } from 'react';

// Floating right-rail panel showing document-level status notifications
// (Urgent / New / Updated / Active!). Each doc contributes at most one
// entry, prioritized Urgent > New > Updated > Active, then alphabetical.
// Dismissals are tracked at the app level so they survive rerenders but
// not reloads.
export function DocumentNotifications({
  documents,
  dismissedNotifications,
  setDismissedNotifications,
  onOpenDocument,
  playClickSound,
}) {
  const list = useMemo(() => {
    if (!Array.isArray(documents)) return [];
    const acc = [];
    documents.forEach(d => {
      if (d.isArchived) return;
      // respect admin-controlled display flag (default: show)
      if (d.displayStatusNotification === false) return;
      const statuses = Array.isArray(d.statuses) ? d.statuses : (d.status ? [d.status] : []);
      if (statuses.includes('Urgent')) acc.push({ id: `urgent-${d.id}`, docId: d.id, level: 'urgent', text: `🚨 ${d.name} | Urgent Notice` });
      else if (statuses.includes('New')) acc.push({ id: `new-${d.id}`, docId: d.id, level: 'new', text: `🆕 ${d.name} | View Now` });
      else if (statuses.includes('Updated')) acc.push({ id: `updated-${d.id}`, docId: d.id, level: 'updated', text: `🔄 ${d.name} | Updated Information` });
      else if (statuses.includes('Active!')) acc.push({ id: `active-${d.id}`, docId: d.id, level: 'active', text: `🔥 ${d.name} | is active` });
    });
    const priority = { urgent: 0, new: 1, updated: 2, active: 3 };
    acc.sort((a, b) => {
      const pa = priority.hasOwnProperty(a.level) ? priority[a.level] : 99;
      const pb = priority.hasOwnProperty(b.level) ? priority[b.level] : 99;
      if (pa !== pb) return pa - pb;
      return String(a.text).localeCompare(String(b.text));
    });
    return acc;
  }, [documents]);

  const visible = list.filter(n => !dismissedNotifications.includes(n.id));

  return (
    <div className="fixed right-4 z-30 top-20 max-w-xs w-80">
      {visible.length > 0 && (
        <div className="space-y-2">
          {visible.map(n => (
            <div
              key={n.id}
              className={`flex items-center justify-between p-2 rounded text-sm ${
                n.level === 'urgent'
                  ? 'bg-red-50 border border-red-200 text-red-800'
                  : (n.level === 'new' || n.level === 'active')
                    ? 'bg-green-50 border border-green-200 text-green-800'
                    : n.level === 'updated'
                      ? 'bg-yellow-50 border border-yellow-200 text-yellow-800'
                      : ''
              }`}
            >
              <div className="flex-1 pr-2">{n.text}</div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    playClickSound();
                    const doc = documents.find(d => d.id === n.docId);
                    if (doc) onOpenDocument(doc);
                  }}
                  className="text-xs underline text-gray-700"
                >
                  Open
                </button>
                <button
                  onClick={() => setDismissedNotifications(prev => [...prev, n.id])}
                  className="text-xs text-gray-500 px-2"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Red unread-ticket banner that appears below the Remote Support pill
// whenever there are tickets updated since the front desk last looked.
// Hidden while either support drawer is open (so it doesn't stack on
// top of the drawer the user already sees).
export function TicketUpdateBanner({
  supportUnreadCount,
  supportDrawerOpen,
  ticketsDrawerOpen,
  onOpenTickets,
}) {
  if (ticketsDrawerOpen || supportDrawerOpen || supportUnreadCount <= 0) return null;
  return (
    <div className="fixed top-20 right-4 z-30 max-w-xs">
      <button
        onClick={onOpenTickets}
        className="w-full text-left bg-red-50 border border-red-200 text-red-900 rounded-lg shadow-md px-3 py-2 hover:bg-red-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-80" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-600" />
          </span>
          <span className="text-xs font-bold uppercase tracking-wide">Ticket Update</span>
        </div>
        <div className="text-sm mt-1">
          {supportUnreadCount} ticket{supportUnreadCount > 1 ? 's' : ''} updated since you last checked.
        </div>
        <div className="text-[11px] text-red-700 mt-0.5">Click to view.</div>
      </button>
    </div>
  );
}
